import { Formik } from "formik";
import { Form, Row, Col, Card } from "react-bootstrap";
import React, { useState } from "react";
import * as yup from "yup";
import SecurityCodeInput from "../../components/auth/SecurityCodeInput";
import AppButton from "../../views/components/Common/AppButton";
import useJwt from "../../services/useJwt";
import Message from "../../utility/notifications/Message";

const schema = yup.object().shape({
  twoFactorVerificationCode: yup.string().required().label("2FA code"),
});

interface Configure2FAProps {
  setupGoogleAuthenticatorImageUrl?: string;
  googleAuthenticatorKey?: string;
  setupAuthenticatorManualEntryKey?: string;
  email?: string;
  password?: string;
  onSuccess?: (data?: any) => void;
  onProfilePage?: boolean;
}

type TwoFactorMethod = "authenticator" | "email";

const Configure2FA = ({
  setupGoogleAuthenticatorImageUrl,
  googleAuthenticatorKey,
  setupAuthenticatorManualEntryKey,
  email,
  password,
  onSuccess,
  onProfilePage = false,
}: Configure2FAProps) => {
  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  // Add state to store authenticator data for profile page
  const [profileAuthenticatorData, setProfileAuthenticatorData] = useState<{
    setupGoogleAuthenticatorImageUrl?: string;
    googleAuthenticatorKey?: string;
    setupAuthenticatorManualEntryKey?: string;
  } | null>(null);
  const [isLoadingAuthenticator, setIsLoadingAuthenticator] = useState(false);

  const handleMethodSelection = async (method: TwoFactorMethod) => {
    setSelectedMethod(method);

    if (onProfilePage && method === "authenticator") {
      // On profile page, fetch the authenticator data
      setIsLoadingAuthenticator(true);
      try {
        const response = await useJwt.getGoogleAuthImageUrl();

        setProfileAuthenticatorData({
          setupGoogleAuthenticatorImageUrl:
            response.data.result.setupGoogleAuthenticatorImageUrl,
          googleAuthenticatorKey: response.data.result.googleAuthenticatorKey,
          setupAuthenticatorManualEntryKey:
            response.data.result.setupAuthenticatorManualEntryKey,
        });
      } catch (err: any) {
        Message(
          err.response?.data?.error?.message ||
            "Failed to load authenticator setup data",
          err.response?.data?.error?.details || "",
          "error"
        );
        setSelectedMethod(null); // Go back to method selection
      } finally {
        setIsLoadingAuthenticator(false);
      }
      return;
    }

    if (onProfilePage && method === "email") {
      setIsSubmitting(true);
      return useJwt
        .setEmailAuthenticatorSettingOnAuth()
        .then(() => {
          setIsSubmitting(false);
          // Message("Email 2FA has been configured successfully.", "", "success");

          if (onSuccess) {
            onSuccess();
          }
        })
        .catch((err) => {
          setIsSubmitting(false);
          Message(
            err.response?.data?.error?.message ||
              "Failed to configure email 2FA",
            err.response?.data?.error?.details || "",
            "error"
          );
        });
    }

    if (method === "email") {
      // Configure email 2FA immediately
      setIsSubmitting(true);
      useJwt
        .setEmailAuthenticatorSetting({
          email,
          password,
        })
        .then(() => {
          setEmailConfigured(true);
          setIsSubmitting(false);
          Message(
            "Email 2FA has been configured successfully. Please check your email for the verification code.",
            "",
            "success"
          );
        })
        .catch((err) => {
          setIsSubmitting(false);
          Message(
            err.response?.data?.error?.message ||
              "Failed to configure email 2FA",
            err.response?.data?.error?.details || "",
            "error"
          );
        });
    }
  };

  const handleAuthenticatorSubmit = async (values: any) => {
    setIsSubmitting(true);

    try {
      const response = await useJwt.setAuthenticatorKey({
        email,
        password,
        verificationCode: values.twoFactorVerificationCode,
        authenticatorKey:
          googleAuthenticatorKey || values.googleAuthenticatorKey,
      });

      if (response.status === 200) {
        // After successful authenticator setup, immediately log the user in
        try {
          const loginResponse = await useJwt.login({
            userNameOrEmailAddress: email,
            password,
            twoFactorVerificationCode: values.twoFactorVerificationCode,
            twoFactorRememberClient: false,
          });

          if (loginResponse.status === 200) {
            if (onSuccess) {
              onSuccess(loginResponse.data);
            }
          }
        } catch (loginErr: any) {
          Message(
            "2FA setup successful, but automatic login failed. Please try logging in manually.",
            loginErr.response?.data?.error?.details || "",
            "warning"
          );
          // Still call onSuccess but with the original setup response
          // This will at least get them back to the login screen
          if (onSuccess) {
            onSuccess(response.data);
          }
        }
      }
    } catch (err: any) {
      Message(
        err.response?.data?.error?.message || "Failed to setup authenticator",
        err.response?.data?.error?.details || "",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAuthenticatorSubmitProfile = async (values: any) => {
    setIsSubmitting(true);

    try {
      const response = await useJwt.setAuthenticatorKeyOnAuth({
        verificationCode: values.twoFactorVerificationCode,
        authenticatorKey:
          (onProfilePage
            ? profileAuthenticatorData?.googleAuthenticatorKey
            : googleAuthenticatorKey) || values.googleAuthenticatorKey,
      });

      if (response.status === 200) {
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err: any) {
      Message(
        err.response?.data?.error?.message || "Failed to setup authenticator",
        err.response?.data?.error?.details || "",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubmit = async (values: any) => {
    setIsSubmitting(true);

    try {
      const response = await useJwt.login({
        userNameOrEmailAddress: email,
        password,
        twoFactorVerificationCode: values.twoFactorVerificationCode,
        twoFactorRememberClient: false,
      });

      if (response.status === 200) {
        if (onSuccess) {
          onSuccess(response.data);
        }
      }
    } catch (err: any) {
      Message(
        err.response?.data?.error?.message || "Failed to verify email 2FA",
        err.response?.data?.error?.details || "",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMethodSelection = () => (
    <div className="my-5">
      <h3 className="text-center mb-4">Choose Your 2FA Method</h3>
      <p className="text-center mb-4">
        For your security, please set up two-factor authentication before
        logging in.
      </p>

      <Row className="justify-content-center">
        <Col md={12} className="mb-3">
          <Card
            className="h-100 cursor-pointer border-2"
            onClick={() => handleMethodSelection("authenticator")}
            style={{ cursor: "pointer" }}
          >
            <Card.Body className="text-center p-4">
              <div className="mb-3">
                <i className="fas fa-mobile-alt fa-3x text-primary"></i>
              </div>
              <h5>Authenticator App</h5>
              <p className="text-muted">
                Use Google Authenticator, Microsoft Authenticator, or similar
                apps to generate codes
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={12} className="mb-3">
          <Card
            className="h-100 cursor-pointer border-2"
            onClick={() => handleMethodSelection("email")}
            style={{ cursor: "pointer" }}
          >
            <Card.Body className="text-center p-4">
              <div className="mb-3">
                <i className="fas fa-envelope fa-3x text-primary"></i>
              </div>
              <h5>Email</h5>
              <p className="text-muted">Receive verification codes via email</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderAuthenticatorSetup = () => {
    // Get the appropriate data source based on whether we're on profile page
    const imageUrl = onProfilePage
      ? profileAuthenticatorData?.setupGoogleAuthenticatorImageUrl
      : setupGoogleAuthenticatorImageUrl;
    const authKey = onProfilePage
      ? profileAuthenticatorData?.googleAuthenticatorKey
      : googleAuthenticatorKey;
    const manualKey = onProfilePage
      ? profileAuthenticatorData?.setupAuthenticatorManualEntryKey
      : setupAuthenticatorManualEntryKey;

    if (onProfilePage && isLoadingAuthenticator) {
      return (
        <>
          <div className="my-5">
            <div className="text-center mb-4">
              <AppButton
                type="button"
                title="Back to method selection"
                buttonSize="sm"
                variant="back"
                onClick={() => setSelectedMethod(null)}
              />
            </div>

            <div className="text-center">
              <i className="fas fa-spinner fa-spin fa-2x text-primary mb-3"></i>
              <p>Loading authenticator setup...</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="my-5">
          <div className="text-center mb-4">
            <AppButton
              type="button"
              title="Back to method selection"
              buttonSize="sm"
              variant="back"
              onClick={() => setSelectedMethod(null)}
            />
          </div>

          <p className="text-center">
            Scan the QR code below with your authenticator app, then enter the
            verification code.
          </p>
        </div>

        <Formik
          enableReinitialize={true}
          initialValues={{
            googleAuthenticatorKey: authKey || "",
            twoFactorVerificationCode: "",
          }}
          validationSchema={schema}
          onSubmit={
            onProfilePage
              ? handleAuthenticatorSubmitProfile
              : handleAuthenticatorSubmit
          }
        >
          {({ handleSubmit }) => (
            <Form className="auth-login-form mt-2" onSubmit={handleSubmit}>
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-center flex-wrap mt-2">
                  <img width={240} src={imageUrl} alt="2fa-qr" />
                </div>

                {manualKey && (
                  <div className="d-flex justify-content-center mb-5 w-100 p-0">
                    <small className="text-muted">
                      Or manually copy the code: <strong>{manualKey}</strong>
                    </small>
                  </div>
                )}

                <SecurityCodeInput
                  name="twoFactorVerificationCode"
                  label="Enter the code from your authenticator app"
                />
              </div>

              <AppButton
                loading={isSubmitting}
                disabled={isSubmitting}
                type="submit"
                title={onProfilePage ? "Confirm" : "Confirm and login"}
                buttonSize="lg"
                className="w-100"
              />
            </Form>
          )}
        </Formik>
      </>
    );
  };

  const renderEmailSetup = () => (
    <>
      <div className="my-5">
        <div className="text-center mb-4">
          <AppButton
            type="button"
            title="Back to method selection"
            buttonSize="sm"
            variant="back"
            onClick={() => setSelectedMethod(null)}
          />
        </div>

        {emailConfigured ? (
          <div className="alert alert-success text-center">
            <i className="fas fa-check-circle me-2"></i>
            Email 2FA has been configured! Please check your email for the
            verification code.
          </div>
        ) : (
          <div className="text-center">
            <i className="fas fa-spinner fa-spin fa-2x text-primary mb-3"></i>
            <p>Configuring email 2FA...</p>
          </div>
        )}
      </div>

      {emailConfigured && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            twoFactorVerificationCode: "",
          }}
          validationSchema={schema}
          onSubmit={handleEmailSubmit}
        >
          {({ handleSubmit }) => (
            <Form className="auth-login-form mt-2" onSubmit={handleSubmit}>
              <div className="mb-4">
                <SecurityCodeInput
                  name="twoFactorVerificationCode"
                  label="Enter the code from your email"
                />
              </div>

              <AppButton
                loading={isSubmitting}
                disabled={isSubmitting}
                type="submit"
                title="Confirm and login"
                buttonSize="lg"
                className="w-100"
              />
            </Form>
          )}
        </Formik>
      )}
    </>
  );

  if (!selectedMethod) {
    return renderMethodSelection();
  }

  if (selectedMethod === "authenticator") {
    return renderAuthenticatorSetup();
  }

  if (selectedMethod === "email") {
    return renderEmailSetup();
  }

  return null;
};

export default Configure2FA;
